import type {
  AiChatStateResponse,
  AiRequest,
  AiResponse,
  AiTaskCreateResponse,
} from '@store/reducers/ai/types'
import axios, { type AxiosResponse, type CancelTokenSource } from 'axios'
import type { SagaIterator } from 'redux-saga'
import { call } from 'redux-saga/effects'

export const AI_CHAT_BASE_URL = import.meta.env.VITE_AI_CHAT_HOST

const AI_CHAT_REQUEST_TIMEOUT_MS = 60_000

const getAuthHeaders = () => ({
  'X-Auth-Token': localStorage.getItem('token'),
})

const getLastUserMessageContent = (data: AiRequest): string | null =>
  [...data.messages]
    .reverse()
    .find(({ role, content }) => role === 'user' && content.trim().length > 0)
    ?.content ?? null

const sendAIMessage = (
  data: AiRequest,
  cancelToken: CancelTokenSource['token']
): Promise<AxiosResponse<AiResponse | AiTaskCreateResponse>> =>
  axios.post(
    '/chat',
    {
      projectId: data.projectId,
      content: getLastUserMessageContent(data) ?? '',
    },
    {
      baseURL: AI_CHAT_BASE_URL,
      headers: getAuthHeaders(),
      timeout: AI_CHAT_REQUEST_TIMEOUT_MS,
      cancelToken,
    }
  )

const getAIChatState = (
  projectId: string,
  cancelToken?: CancelTokenSource['token']
): Promise<AxiosResponse<AiChatStateResponse>> =>
  axios.get('/chat', {
    baseURL: AI_CHAT_BASE_URL,
    headers: getAuthHeaders(),
    params: { projectId },
    cancelToken,
  })

export function* performAIRequest(
  requestData: AiRequest,
  cancelToken: CancelTokenSource['token']
): SagaIterator<AxiosResponse<AiResponse | AiTaskCreateResponse>> {
  const response: AxiosResponse<AiResponse | AiTaskCreateResponse> = yield call(
    sendAIMessage,
    requestData,
    cancelToken
  )
  return response
}

export function* fetchAIChatState(
  projectId: string,
  cancelToken?: CancelTokenSource['token']
): SagaIterator<AxiosResponse<AiChatStateResponse>> {
  const response: AxiosResponse<AiChatStateResponse> = yield call(
    getAIChatState,
    projectId,
    cancelToken
  )

  return response
}
